/** ... keep header comment ... */
import { useLocation, useNavigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';

import Footer from '../components/Footer';

const PLUTO_LOGO_PATH = `${process.env.PUBLIC_URL || ''}/PlutoTV_Logo_1080x1080_RGB_Plutonium.ai.png`;

const formatDisplayDate = (value) => {
  if (!value) {
    return 'Date TBD';
  }
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
};

const formatDisplayTime = (timeSlot) => {
  if (!timeSlot) {
    return 'Time TBD';
  }
  const [hourStr, minuteStr] = timeSlot.split(':');
  const hour = Number(hourStr);
  const minute = Number(minuteStr);
  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return timeSlot;
  }
  const date = new Date();
  date.setHours(hour, minute, 0, 0);
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const DetailRow = ({ label, value }) => (
  <Stack spacing={0.5}>
    <Typography variant="subtitle2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1">{value || 'TBD'}</Typography>
  </Stack>
);

const BookingConfirmationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const bookingDetails = location.state;
  const hasDetails = Boolean(bookingDetails);

  const allocatedSlots = bookingDetails?.allocatedSlots || [];
  const confirmationMessage = bookingDetails?.confirmationMessage
    ? bookingDetails.confirmationMessage
    : hasDetails
    ? `Your smash squad is confirmed for ${bookingDetails.roomName || 'a rage room'} on ${formatDisplayDate(
        bookingDetails.date
      )} at ${formatDisplayTime(bookingDetails.timeSlot)}.`
    : '';

  const handleBookAnother = () => {
    navigate('/book');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleManageBooking = () => {
    if (bookingDetails?.bookingId) {
      navigate(`/book/cancel/${bookingDetails.bookingId}`);
    }
  };

  return (
    <>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Stack spacing={4} alignItems="center">
          <Box
            component="img"
            src={PLUTO_LOGO_PATH}
            alt="Pluto TV logo"
            sx={{
              height: { xs: 72, sm: 96, md: 128 },
              width: 'auto',
              filter: 'drop-shadow(0 10px 25px rgba(0,0,0,0.6))',
            }}
          />
          {hasDetails ? (
            <Card sx={{ width: '100%' }} variant="outlined">
              <CardContent>
                <Stack spacing={3}>
                  <Stack spacing={1} alignItems="center" textAlign="center">
                    <Typography variant="h3">Booking Confirmed</Typography>
                    <Typography color="text.secondary" sx={{ maxWidth: 640 }}>
                      {confirmationMessage}
                    </Typography>
                  </Stack>
                  <Divider />
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <DetailRow
                        label="Room"
                        value={bookingDetails.roomName || 'Rage Room'}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailRow
                        label="City / Location"
                        value={
                          bookingDetails.locationName && bookingDetails.city
                            ? `${bookingDetails.locationName} • ${bookingDetails.city}`
                            : bookingDetails.locationName || bookingDetails.city || 'To be assigned'
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailRow
                        label="Session Time"
                        value={`${formatDisplayDate(bookingDetails.date)} @ ${formatDisplayTime(
                          bookingDetails.timeSlot
                        )}`}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailRow
                        label="Crew Size"
                        value={
                          bookingDetails.resolvedGuestCount
                            ? `${bookingDetails.resolvedGuestCount} guest${
                                bookingDetails.resolvedGuestCount === 1 ? '' : 's'
                              }`
                            : 'Pending'
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailRow
                        label="Session Label"
                        value={bookingDetails.sessionLabel || '30-Min Smash Session'}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailRow
                        label="Venue Address"
                        value={bookingDetails.locationAddress || 'Provided in your confirmation email'}
                      />
                    </Grid>
                  </Grid>
                  {allocatedSlots.length > 0 && (
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Slots Locked
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {allocatedSlots.map((slot) => (
                          <Chip key={slot} label={slot} color="primary" size="small" />
                        ))}
                      </Stack>
                    </Stack>
                  )}
                {/*
                  {bookingDetails?.bookingId && (
                    <Alert severity="info">
                      Confirmation ID: <strong>{bookingDetails.bookingId}</strong>. Keep this handy if
                      you need to update or cancel your reservation.
                    </Alert>
                  )}
                  */}
                  <Typography variant="body2" color="primary">
                    Please arrive 15-minutes before your session starts. You will also receive a confirmation email with everything you need for check-in. 
                  </Typography>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                  >
                    <Button variant="contained" size="large" onClick={handleBookAnother} fullWidth>
                      Book Another Session
                    </Button>
                    {bookingDetails?.bookingId && (
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={handleManageBooking}
                        fullWidth
                      >
                        Manage This Booking
                      </Button>
                    )}
                    <Button variant="text" size="large" onClick={handleGoHome} fullWidth>
                      Back to Home
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ) : (
            <Alert
              severity="info"
              sx={{ width: '100%' }}
              action={
                <Button color="inherit" size="small" onClick={handleBookAnother}>
                  Return to Booking
                </Button>
              }
            >
              We could not find the booking details for this confirmation page. Please return to the
              booking form and complete a reservation.
            </Alert>
          )}
        </Stack>
      </Container>
      <Footer />
    </>
  );
};

export default BookingConfirmationPage;